import { AxiosResponse } from "axios";
import { ApiUsersResponse } from "../types/apiusersresponse";
import ApiInterceptor from "./api";
import {InviteUserResponse} from "../types/inviteuserresponse";

const imagesService = {
  async getAllImagesPaginated(
    useAuth: boolean,
    pageNumber: number,
    pageSize: number,
    dateFrom: string | null,
    dateTo: string | null
  ): Promise<any> {
    const axiosInstance = ApiInterceptor.service(useAuth);
    try {
      const response: AxiosResponse = await axiosInstance.get(
        `/Images?pageNumber=${pageNumber}&pageSize=${pageSize}&dateFrom=${dateFrom || ""}&dateTo=${dateTo || ""}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  },

  async uploadPhoto(
    image: File,
    longitude: number,
    latitude: number,
    useAuth: boolean,
    siteName: string,
  ): Promise<any> {
    const axiosInstance = ApiInterceptor.service(useAuth);

    try {

      const formData = new FormData();
      formData.append("image", image);
      formData.append("longitude", longitude.toString());
      formData.append("latitude", latitude.toString());
      formData.append("siteName", siteName);

      const response = await axiosInstance.post("/Images/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Ensure correct content type
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error uploading photo:", error);
      throw error;
    }
  },

  async deleteRequest(requestId: string) {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.delete(`/Images/${requestId}`);
      return response.data;
    } catch (error) {
      console.error("Error with deleting post by admin:", error);
      throw error;
    }
  },

  async setRating(requestId: string, rating: number, useAuth: boolean): Promise<any> {
    const axiosInstance = ApiInterceptor.service(useAuth);
    const userId = "336d2227-cccb-4750-bede-7adc94f35283"; // TODO: set user id
    try {
      const response = await axiosInstance.post("/Images/rate", {
        requestId,
        userId,
        rating,
      });
      return response.data;
    } catch (error) {
      console.error("Error setting rating:", error);
      throw error;
    }
  },

  async getPreviousRequests(): Promise<any> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.get("/Images/previous-requests");
      return response.data;
    } catch(error) {
      console.error("Error with fetching previous requests:", error);
      throw error;
    }
  },

  async getUsersData(pageNumber: number, pageSize: number): Promise<ApiUsersResponse> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.get(`/Users/user-stats?pageSize=${pageSize}&pageNumber=${pageNumber}`);
      return response.data;
    } catch(error) {
      console.error("Error with fetching previous requests:", error);
      throw error;
    }
  },

  /**
   * Makes an HTTP GET request to fetch the CSV file.
   * @returns {Promise<AxiosResponse>} The Axios response containing the CSV file.
   */
  async getCsvFile(): Promise<AxiosResponse<Blob>> {
    const axiosInstance = ApiInterceptor.service(true); // Use interceptor with auth
    try {
      return await axiosInstance.get<Blob>(`/Users/get-csv`, {
        responseType: "blob", // Ensure response is treated as a file (Blob)
      });
    } catch (error) {
      console.error("Error fetching CSV file:", error);
      throw error;
    }
  },

  async overrideImageName(requestId: string, newName: string, hasEnlistedEndangeredSpecies: boolean) {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.post("/Images/overrideName", {
        requestId: requestId,
        newName: newName,
        hasEnlistedEndangeredSpecies: hasEnlistedEndangeredSpecies
      });
      return response.data;
    } catch(error) {
      console.error("Error with overriding name by admin:", error);
      throw error;
    }
  },
  async overrideThreatStatus(requestId: string) {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.get(`/Images/change-status/${requestId}`);
      return response.data;
    } catch(error) {
      console.error("Error with overriding status by admin:", error);
      throw error;
    }
  },
  async overrideImageNameInvasive(requestId: string, newName: string, hasEnlistedEndangeredSpecies: boolean) {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.post("/Images/overrideName/invasive", {
        requestId: requestId,
        newName: newName,
        hasEnlistedEndangeredSpecies: hasEnlistedEndangeredSpecies
      });
      return response.data;
    } catch(error) {
      console.error("Error with overriding name by admin:", error);
      throw error;
    }
  },

  async inviteNewUser(newEmail: string): Promise<InviteUserResponse> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.post("/Users/invite", {
          email: newEmail
      });
      return response.data;
    } catch(error) {
      console.error("Error with sending invite to new user", error);
      throw error;
    }
  },

  async getAdminNotifications(): Promise<any[]> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      const response = await axiosInstance.get('/Users/admin-notifications');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
      throw error;
    }
  },

  async updateAdminNotification(userId: string, userName: string, email: string, notificationsEnabled: boolean): Promise<void> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      await axiosInstance.put(`/Users/admin-notifications/${userId}`, {
        userId,
        userName,
        email,
        notificationsEnabled,
      });
    } catch (error) {
      console.error('Error updating admin notification:', error);
      throw error;
    }
  },

  async disableAllAdminNotifications(): Promise<void> {
    const axiosInstance = ApiInterceptor.service(true);
    try {
      await axiosInstance.post('/Users/admin-notifications/disable-all');
    } catch (error) {
      console.error('Error disabling all admin notifications:', error);
      throw error;
    }
  },
};

export default imagesService;